/**
 * <back-in-stock> — "Notify me when back in stock" for a sold-out variant, wired to
 * Klaviyo Back in Stock (Client API). Markup + styles live in snippets/back-in-stock.liquid.
 *
 * Loaded as a module (importmap "@theme/back-in-stock") so the element is defined on the
 * product page AND inside the quick-add dialog (quick-add injects the fetched product HTML
 * via morph, which does not execute inline <script>, so the definition must be global).
 *
 * Listens for the theme 'variant:update' event (scoped to the closest section/dialog/
 * product-card, mirroring product-form.js) and toggles between the .purchase-controls__buy
 * container and its own restock UI based on the selected variant's availability.
 */
if (!customElements.get('back-in-stock')) {
  // Klaviyo public key lives in the onsite script URL: .../onsite/js/<KEY>/klaviyo.js
  const klaviyoKey = () => {
    const scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (let i = 0; i < scripts.length; i++) {
      const m = scripts[i].src.match(/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/);
      if (m) return m[1];
    }
    return null;
  };

  const show = (el) => { if (el) el.hidden = false; };
  const hide = (el) => { if (el) el.hidden = true; };

  customElements.define('back-in-stock', class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-bis-trigger]');
      this.form = this.querySelector('[data-bis-form]');
      this.email = this.querySelector('[data-bis-email]');
      this.okMsg = this.querySelector('[data-bis-success]');
      this.errMsg = this.querySelector('[data-bis-error]');
      this.onClick = this.onClick.bind(this);
      this.onVariant = this.onVariant.bind(this);
      // Scope to the same target the theme's product form uses, so a quick-add
      // dialog or product card doesn't react to another form's variant change.
      this.scope = this.closest('.shopify-section, dialog, product-card') || document;
      this.addEventListener('click', this.onClick);
      this.scope.addEventListener('variant:update', this.onVariant);
    }

    disconnectedCallback() {
      if (this.scope) this.scope.removeEventListener('variant:update', this.onVariant);
    }

    // The sibling container holding the add-to-cart / accelerated-checkout buttons.
    buyButtons() {
      const parent = this.parentElement;
      return parent ? parent.querySelector('.purchase-controls__buy') : null;
    }

    reset() {
      show(this.trigger);
      hide(this.form);
      hide(this.okMsg);
      hide(this.errMsg);
      if (this.email) this.email.value = '';
    }

    onVariant(e) {
      const v = e && e.detail && e.detail.resource;
      if (!v || v.id == null) return;
      this.setAttribute('data-variant-id', v.id);
      const soldOut = v.available === false;
      this.reset();
      this.hidden = !soldOut;
      const buy = this.buyButtons();
      if (buy) buy.hidden = soldOut;
    }

    onClick(e) {
      if (e.target.closest('[data-bis-trigger]')) {
        hide(this.trigger);
        show(this.form);
        if (this.email) this.email.focus();
        return;
      }

      const submit = e.target.closest('[data-bis-submit]');
      if (!submit) return;

      const email = ((this.email && this.email.value) || '').trim();
      const variant = this.getAttribute('data-variant-id');
      const key = klaviyoKey();
      hide(this.errMsg);

      if (!email || email.indexOf('@') === -1) { show(this.errMsg); if (this.email) this.email.focus(); return; }
      if (!key || !variant) { show(this.errMsg); return; }

      submit.disabled = true;
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } }
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant } }
          }
        }
      };

      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify(payload)
      })
        .then((r) => r.text().then(() => ({ ok: r.ok })))
        .then((res) => {
          submit.disabled = false;
          if (res.ok) {
            hide(this.form);
            show(this.okMsg);
          } else {
            show(this.errMsg);
          }
        })
        .catch(() => { submit.disabled = false; show(this.errMsg); });
    }
  });
}
